'use client';

import { createLogger } from '@svenskaspelab/nova-core/log';
import { useParams, usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useContext, useEffect, useRef } from 'react';
import { getCouponId, toOutcome } from '../../../../../schema/coupon/entry.js';
import '../../../../../schema/draw/entry.js';
import { isCouponProduct } from '../../../../../schema/product/entry.js';
import { getInstanceClient } from '../../../../di/entry.browser.js';
import { DEFAULT_DRAW_ID, getValidatedQueryParameters, parseDrawId } from '../../../../lib/entry.browser.js';
import { ConsumerContext } from '../../components/ConsumerProvider.js';
import { useEngine } from '../../engine/entry.js';
import { useCoupon } from '../hooks/useCoupon.js';
import { useCouponEventCount } from '../hooks/useCouponEventCount.js';
import { useCouponId } from '../hooks/useCouponId.js';
import { useCouponIndices } from '../hooks/useCouponIndices.js';
import { useHasHydratedCouponStore } from '../hooks/useHasHydratedCouponStore.js';
import { useNumberOfPopulatedCoupons } from '../hooks/useNumberOfPopulatedCoupons.js';
const logger = createLogger('poolbetting.share-coupon-url');
/** Query params consumed by the share-coupon flow and removed from the URL afterwards. */
const CONSUMED_URL_PARAMS = ['outcomes', 'signs', 'betAmount'];
/**
 * Removes the query params consumed by the share-coupon flow, leaving every other param untouched.
 *
 * @param searchParams - The current URL search params.
 * @returns The remaining search string, without a leading "?".
 */
export function stripConsumedUrlParams(searchParams) {
  const remainingParams = new URLSearchParams(searchParams.toString());
  for (const consumedParam of CONSUMED_URL_PARAMS) {
    remainingParams.delete(consumedParam);
  }
  return remainingParams.toString();
}
/**
 * Decodes a string of encoded outcomes signs into a Bet object.
 *
 * @param input - The encoded string containing outcome signs.
 * @returns A Bet object representing the decoded outcomes.
 */
export function decodeSigns(input) {
  // TODO: This decoder should maybe be moved do domain layer?
  const decoded = decodeURIComponent(input);
  const [decodedWithoutEnd] = decoded.split(' ');
  const entries = decodedWithoutEnd?.split(',');
  const outcomesMap = new Map();
  let maxIndex = 0;
  for (const entry of entries ?? []) {
    const [key, rawValue] = entry.split(':');
    const index = key === undefined ? Number.NaN : Number.parseInt(key, 10) - 1;
    if (Number.isNaN(index) || !rawValue) {
      continue;
    }
    const symbols = rawValue.split(';');
    const isValid = symbols.every(v => v === '1' || v === 'X' || v === '2');
    if (!isValid) {
      continue;
    }
    const values = symbols.map(v => {
      switch (v) {
        case '1':
          return toOutcome(0);
        case 'X':
          return toOutcome(1);
        case '2':
          return toOutcome(2);
        default:
          throw new Error(`Unexpected value "${v}" even after validation`);
      }
    });
    outcomesMap.set(index, [values]);
    if (index > maxIndex) {
      maxIndex = index;
    }
  }
  const outcomes = [];
  for (let i = 0; i <= maxIndex; i++) {
    outcomes[i] = outcomesMap.get(i) ?? [[]];
  }
  return outcomes;
}
function getParsedCouponId({
  searchParams,
  couponId,
  pathDrawNumber,
  pathProduct
}) {
  const {
    draw: queryDraw,
    product: queryProduct
  } = getValidatedQueryParameters('draw', 'product')(searchParams);
  if (pathDrawNumber !== undefined && queryDraw !== undefined && pathDrawNumber !== queryDraw) {
    logger.warn('Draw number mismatch between pathname and searchParams. Using pathname value.', {
      pathDrawNumber,
      queryDraw
    });
  }
  if (pathProduct !== undefined && queryProduct !== undefined && pathProduct !== queryProduct) {
    logger.warn('Product mismatch between pathname and searchParams. Using pathname value.', {
      pathProduct,
      queryProduct
    });
  }
  const draw = pathDrawNumber ?? queryDraw;
  const product = pathProduct ?? queryProduct;
  const type = 'standard';
  const parsedCouponId = product && draw ? getCouponId(product, draw, type) : couponId;
  return parsedCouponId;
}
function getParsedCouponIndex(searchParams, couponIndex) {
  const {
    coupon
  } = getValidatedQueryParameters('draw', 'product')(searchParams);
  return coupon ?? couponIndex;
}
/**
 * Parses the pathname `drawId` route param into product/drawNumber overrides.
 * Only returns values that were explicitly present in the pathname — defaults from
 * `parseDrawId` (e.g. fallback product, DEFAULT_DRAW_ID) are not treated as overrides.
 *
 * @param drawIdParam - The raw `drawId` route param value, formatted as "drawNumber-product" or just "drawNumber".
 * @param definition - The coupon definition object.
 * @returns Object with optional `drawNumber` and `product` extracted from pathname.
 */
export function getPathnameOverrides(drawIdParam, definition) {
  if (!drawIdParam || drawIdParam === DEFAULT_DRAW_ID) {
    return {
      drawNumber: undefined,
      product: undefined
    };
  }
  const parts = drawIdParam.split('-');
  const lastPart = parts.at(-1);
  const hasProductInPath = parts.length > 1 && isCouponProduct(lastPart);
  const parsed = parseDrawId(drawIdParam, definition.model);
  const drawNumber = parsed.drawNumber === DEFAULT_DRAW_ID ? undefined : parsed.drawNumber;
  const product = hasProductInPath ? parsed.product : undefined;
  return {
    drawNumber,
    product
  };
}
function getCouponEntity(searchParams, definition) {
  let couponEntity = {};
  const betAmountFromUrl = searchParams.get('betAmount');
  const betAmount = betAmountFromUrl === null ? undefined : Number.parseInt(betAmountFromUrl, 10);
  if (betAmount && betAmount >= definition.minBetAmount && betAmount <= definition.maxBetAmount) {
    couponEntity.betAmount = betAmount;
  }
  if (Object.keys(couponEntity).length === 0) {
    couponEntity = undefined;
  }
  return couponEntity;
}
function getBoardEntity(searchParams, definition) {
  let boardEntity = {
    values: []
  };
  const {
    outcomeGroupCount
  } = definition.model.outcomes;
  let outcomesUrl = searchParams.get('outcomes');
  outcomesUrl ??= searchParams.get('signs');
  const outcomes = outcomesUrl ? decodeSigns(outcomesUrl) : undefined;
  // TODO: handle outcomeGroupCount > 1 for later product
  if (outcomes && outcomeGroupCount === 1) {
    const outcomePayloadValues = [];
    for (const [outcomeIndex, outcomeGroup] of outcomes.entries()) {
      const [outcomesForGroup] = outcomeGroup;
      if (outcomesForGroup && outcomesForGroup.length > 0) {
        outcomePayloadValues.push({
          outcomes: outcomesForGroup,
          group: 0,
          // assume only one outcome group for now
          event: outcomeIndex + 1
        });
      }
    }
    boardEntity = {
      values: outcomePayloadValues
    };
  }
  if (boardEntity.values.length === 0) {
    boardEntity = undefined;
  }
  return boardEntity;
}
/**
 * Parses URL parameters to extract coupon and board-related entities.
 *
 * Pathname-derived values (`pathDrawNumber`, `pathProduct`) take priority over
 * the corresponding search params. A mismatch between the two sources is logged.
 *
 * @param root0 - The parameters object.
 * @param root0.searchParams - The URL search parameters.
 * @param root0.couponId - The current coupon ID.
 * @param root0.couponIndex - The current coupon index.
 * @param root0.definition - The coupon definition object.
 * @param root0.pathDrawNumber - Optional draw number extracted from the pathname (takes priority over `?draw=`).
 * @param root0.pathProduct - Optional product extracted from the pathname (takes priority over `?product=`).
 * @returns A tuple containing the parsed coupon ID, coupon index, coupon entity, and board entity.
 */
export function parseUrl({
  searchParams,
  couponId,
  couponIndex,
  definition,
  pathDrawNumber,
  pathProduct
}) {
  const parsedCouponId = getParsedCouponId({
    searchParams,
    couponId,
    pathDrawNumber,
    pathProduct
  });
  const parsedCouponIndex = getParsedCouponIndex(searchParams, couponIndex);
  const couponEntity = getCouponEntity(searchParams, definition);
  const boardEntity = getBoardEntity(searchParams, definition);
  return [parsedCouponId, parsedCouponIndex, couponEntity, boardEntity];
}
/**
 * Consumes the local storage and rehydrates the Zustand coupon store.
 *
 * @returns undefined
 */
export default function ShareCouponUrlConsumer() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();
  const routeParams = useParams();
  const hasHydrated = useHasHydratedCouponStore();
  const hasCalledEventContextRef = useRef(false);
  const hasHandledInitialRef = useRef(false);
  const [couponId, couponIndex] = useCouponId();
  const definition = useEngine();
  const numberOfPopulatedCoupons = useNumberOfPopulatedCoupons({
    couponId
  });
  const eventContext = useContext(ConsumerContext);
  const {
    maxNumberOfCoupons
  } = definition.model;
  const couponIndices = useCouponIndices({
    couponId
  });
  const coupon = useCoupon({
    couponId,
    couponIndex
  });
  const eventCount = useCouponEventCount({
    couponId,
    couponIndex,
    boardIndex: 0
  });
  const {
    callbackFn
  } = eventContext ?? {};
  const removeUrlParams = useCallback(() => {
    if (searchParams.get('outcomes') || searchParams.get('signs')) {
      const newSearch = stripConsumedUrlParams(searchParams);
      const newUrl = `${pathname}${newSearch ? `?${newSearch}` : ''}`;
      router.replace(newUrl, {
        scroll: false
      });
    }
  }, [pathname, router, searchParams]);
  useEffect(() => {
    if (!hasHydrated) {
      return;
    }
    if (hasHandledInitialRef.current) {
      return;
    }
    hasHandledInitialRef.current = true;
    // eslint-disable-next-line complexity
    async function updateCoupon() {
      const {
        drawNumber: pathDrawNumber,
        product: pathProduct
      } = getPathnameOverrides(routeParams.drawId, definition);
      const [parsedCouponId, parsedCouponIndex, couponEntity, boardEntity] = parseUrl({
        searchParams,
        couponId,
        couponIndex,
        definition,
        pathDrawNumber,
        pathProduct
      });
      removeUrlParams();
      let shouldSetNewCoupon = false;
      let shouldResetAllCoupons = false;
      if ((couponEntity || boardEntity) && numberOfPopulatedCoupons === maxNumberOfCoupons && !hasCalledEventContextRef.current) {
        hasCalledEventContextRef.current = true;
        const userInput = await callbackFn?.hasMaxCoupons(undefined);
        if (userInput === 'doNothing') {
          return;
        }
        if (userInput === 'resetAllCoupons') {
          shouldResetAllCoupons = true;
        }
      }
      if ((couponEntity || boardEntity) && numberOfPopulatedCoupons > 0 && !hasCalledEventContextRef.current) {
        hasCalledEventContextRef.current = true;
        const userInput_0 = await callbackFn?.hasPopulatedCoupons(undefined);
        if (userInput_0 === 'addNewCoupon') {
          shouldSetNewCoupon = true;
        } else if (userInput_0 === 'resetAllCoupons') {
          shouldResetAllCoupons = true;
        }
      }
      const updateCouponController = getInstanceClient('IUpdateCouponController');
      const updateCouponBoardController = getInstanceClient('IUpdateCouponBoardController');
      const deleteCouponController = getInstanceClient('IDeleteCouponController');
      const createCouponController = getInstanceClient('ICreateCouponController');
      try {
        let couponIndexFromNewCoupon = undefined;
        if (shouldResetAllCoupons && coupon) {
          // Reverse couponIndices to avoid first coupon being deleted and then all coupons shift index. So need to start from last index
          for (const indexOfCoupon of couponIndices.toReversed()) {
            // eslint-disable-next-line no-await-in-loop
            await deleteCouponController({
              couponId: parsedCouponId,
              couponIndex: indexOfCoupon
            });
          }
          ({
            couponIndex: couponIndexFromNewCoupon
          } = await createCouponController({
            payload: {
              itsProduct: coupon.product,
              couponType: coupon.couponType,
              drawNumber: coupon.drawNumber,
              eventCount: eventCount
            }
          }));
        }
        if (shouldSetNewCoupon && coupon) {
          ({
            couponIndex: couponIndexFromNewCoupon
          } = await createCouponController({
            payload: {
              itsProduct: coupon.product,
              couponType: coupon.couponType,
              drawNumber: coupon.drawNumber,
              eventCount: eventCount
            }
          }));
        }
        if (couponEntity) {
          await updateCouponController(couponEntity, {
            couponId: parsedCouponId,
            couponIndex: couponIndexFromNewCoupon ?? parsedCouponIndex
          });
        }
        if (boardEntity) {
          await updateCouponBoardController({
            outcomes: boardEntity
          }, {
            couponId: parsedCouponId,
            couponIndex: couponIndexFromNewCoupon ?? parsedCouponIndex,
            boardIndex: 0
          });
        }
      } catch (error) {
        logger.warn('Unable to update coupon from URL', {
          error,
          couponId: parsedCouponId,
          couponIndex: parsedCouponIndex
        });
      }
    }
    void updateCoupon();
  }, [couponId, couponIndex, definition, routeParams, searchParams, hasHydrated, maxNumberOfCoupons, numberOfPopulatedCoupons, callbackFn, coupon, couponIndices, eventCount, removeUrlParams]);
  return undefined;
}